import { signOut } from "@homebound/auth-components";
import {
  Css,
  HomeboundLogo,
  MenuItem,
  Navbar,
  NavbarProps,
  NavbarUser,
  px,
  TestIds,
  Tokens,
  useTestIds,
  useToast,
  UseToastProps,
  zIndices,
} from "@homebound/beam";
import { Link, useLocation } from "react-router-dom";
import { useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useCurrentUser } from "src/hooks/useCurrentUser";
import { useStopInternalUserImpersonationMutation } from "src/queries/user.generated";
import { buildGlobalNavItems } from "src/routes/layout/global-nav/buildGlobalNavItems";
import { GlobalNavRightSlot } from "src/routes/layout/global-nav/GlobalNavRightSlot";
import { personalDashboardPaths } from "src/routes/routesDef";

export function GlobalNav() {
  const { name, avatar, user } = useCurrentUser();
  const isBeingImpersonated = user?.isBeingImpersonated ?? false;
  const { pathname } = useLocation();
  const testIds = useTestIds({}, "globalNav");
  const { featureIsEnabled } = useFeatureFlags();
  const { showToast } = useToast();
  const [stopImpersonation] = useStopInternalUserImpersonationMutation();

  async function handleStopImpersonation() {
    await stopImpersonation();
    window.location.reload();
  }

  const navbarProps = getGlobalNavbarProps(
    name,
    avatar,
    pathname,
    featureIsEnabled,
    handleStopImpersonation,
    isBeingImpersonated,
    showToast,
    testIds,
  );

  return (
    <div css={Css.fs0.w100.sticky.top0.z(zIndices.navbar).h(px(topNavHeight)).$} data-testid="globalNavShell">
      <Navbar {...testIds} {...navbarProps} />
    </div>
  );
}

// Intended to be shared by both the above component, and when calling Beam's <NavbarLayout>
export function getGlobalNavbarProps(
  name: string,
  avatar: string,
  pathname: string,
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
  handleStopImpersonation: () => void,
  isBeingImpersonated: boolean,
  showToast: UseToastProps["showToast"],
  tid: TestIds,
): NavbarProps {
  const items = buildGlobalNavItems(pathname, featureIsEnabled);

  const menuItems: MenuItem[] = isBeingImpersonated
    ? [
        {
          label: "Stop Impersonation",
          destructive: true,
          icon: "arrowFromRight",
          onClick: () => {
            handleStopImpersonation();
          },
        },
      ]
    : [
        { label: "Profile", onClick: "https://myaccount.google.com/personal-info" },
        {
          label: "Sign Out",
          onClick: () => {
            signOut().catch((e) => {
              console.error("Failed to sign out", e);
              showToast({ type: "error", message: "Failed to sign out. Please try again or contact support." });
            });
          },
        },
      ];

  const navbarUser: NavbarUser | undefined = name ? { name, picture: avatar, menuItems } : undefined;

  return {
    brand: (
      <Link to={personalDashboardPaths.base} {...tid.homeLink}>
        <HomeboundLogo height={4} fill={Tokens.OnSurface} />
      </Link>
    ),
    hideBrandOnMobile: true,
    items: items,
    rightSlot: <GlobalNavRightSlot />,
    user: navbarUser,
  };
}

export const topNavHeight = 48;
